import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight, Check, Shirt } from "@/components/Icon";
import type { BranchLandingPageData, BranchProductSlot } from "@/data/branchLandingPages";
import { catalogImageBase, imageFallback, imageSrc, useToptexProduct } from "@/hooks/useToptex";

import workwearHero from "@/assets/categories/card-werkkleding.webp";
import workwearSecondary from "@/assets/cat-workwear.webp";
import branchHeroSchilders from "@/assets/header-hero-werkkleding-voor-schilders-stukadoors.webp.asset.json";
import branchIntroSchilders from "@/assets/werkkleding-die-past-bij-het-werk.webp.asset.json";
import branchTeamSchilders from "@/assets/een-professionele-uitstraling-voor-je-hele-team.webp.asset.json";

const SITE_URL = "https://www.magicprint.nl";
const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

const imageMap: Record<string, string> = {
  "header-hero-werkkleding-voor-schilders-stukadoors.webp": branchHeroSchilders.url,
  "werkkleding-die-past-bij-het-werk.webp": branchIntroSchilders.url,
  "een-professionele-uitstraling-voor-je-hele-team.webp": branchTeamSchilders.url,
  "card-werkkleding.webp": workwearHero,
  "cat-workwear.webp": workwearSecondary,
};

const BranchProductCard = ({ selection }: { selection: BranchProductSlot }) => {
  const { data } = useToptexProduct(selection.reference);
  const product = data?.product;
  const preferredColor = product?.colors.find(
    (color) => color.name.toLocaleLowerCase("en") === selection.preferredColor.toLocaleLowerCase("en"),
  );
  const imageBase = selection.hasLocalImage
    ? preferredColor?.imageBase ?? catalogImageBase(selection.reference, selection.preferredColorCode)
    : undefined;
  const brand = product?.brand ?? selection.brand;
  const name = product?.name ?? selection.name;
  const colorCount = product?.colors.length ?? selection.availableColorCount;

  return (
    <Link
      to={`/producten/detail/${encodeURIComponent(selection.reference)}`}
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card transition-all hover:border-primary hover:shadow-md"
      aria-label={`Bekijk ${brand} ${name}`}
    >
      <div className="flex aspect-square items-center justify-center overflow-hidden bg-card">
        {imageBase ? (
          <img
            src={imageSrc(imageBase, 300)}
            onError={imageFallback}
            alt={`${brand} ${name} in ${preferredColor?.name ?? selection.preferredColor}`}
            className="h-full w-full object-contain p-4 transition-transform group-hover:scale-105"
            width={300}
            height={300}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="flex items-center justify-center text-muted-foreground" aria-hidden="true">
            <Shirt className="h-14 w-14 text-primary/55" />
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <p className="mb-1 text-xs text-muted-foreground">
          {brand} • {selection.reference}
        </p>
        <h3 className="mb-3 text-lg font-bold leading-snug text-foreground">
          {name}
        </h3>
        <p className="text-sm leading-relaxed text-muted-foreground">{selection.description}</p>
        {colorCount > 0 && (
          <div className="mt-auto flex items-center gap-2 pt-5">
            {product?.colors && product.colors.length > 0 && (
              <div className="flex -space-x-1" aria-label={`${colorCount} kleuren beschikbaar`}>
                {product.colors.slice(0, 5).map((color) => (
                <span
                  key={color.code}
                  title={color.name}
                  className="h-4 w-4 rounded-full border border-border bg-muted"
                  style={color.hex ? { backgroundColor: color.hex } : undefined}
                />
                ))}
              </div>
            )}
            <span className="text-xs font-medium text-muted-foreground">
              {colorCount} kleuren
            </span>
          </div>
        )}
      </div>
    </Link>
  );
};

const BranchLandingPage = ({ page }: { page: BranchLandingPageData }) => {
  const canonical = `${SITE_URL}/${page.slug}`;
  const heroImage = imageMap[page.heroImage];
  const secondaryImage = imageMap[page.secondaryImage];
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: page.title,
      description: page.description,
      url: canonical,
      isPartOf: { "@type": "WebSite", name: "Magic Print", url: SITE_URL },
      about: { "@type": "Service", name: page.heading, provider: { "@type": "Organization", name: "Magic Print" } },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: page.heading, item: canonical },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ];

  if (!heroImage || !secondaryImage) return null;

  return (
    <>
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Magic Print" />
        <meta property="og:locale" content="nl_NL" />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={page.title} />
        <meta name="twitter:description" content={page.description} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <Navbar />
      <main className="min-h-screen overflow-x-clip bg-background">
        <section className="relative min-h-[680px] overflow-hidden pt-28 md:min-h-[720px] md:pt-36">
          <img
            src={heroImage}
            alt={page.heroImageAlt}
            className="absolute inset-0 h-full w-full object-cover object-right-top md:object-top"
            width={800}
            height={1265}
          />
          <div className="absolute inset-0 bg-surface-dark/70" />
          <div className="container relative z-10 flex min-h-[550px] items-center py-12 md:min-h-[584px] md:py-16">
            <div className="max-w-3xl text-surface-dark-foreground">
              <p className="mb-4 text-base font-semibold text-surface-dark-foreground/90 md:text-lg">
                {page.subheading}
              </p>
              <h1 className="max-w-3xl text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
                {page.heading}
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-surface-dark-foreground/90 md:text-lg">
                {page.heroText}
              </p>
              <ul className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-x-7">
                {page.usps.map((usp) => (
                  <li key={usp} className="flex items-center gap-2 font-semibold">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary">
                      <Check className="h-3 w-3" />
                    </span>
                    {usp}
                  </li>
                ))}
              </ul>
              <Button size="lg" className="mt-8" asChild>
                <Link to="/offerte">
                  Vraag vrijblijvend een offerte aan <ArrowRight />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-16">
          <div className={`container grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 ${page.introduction.image ? "" : "lg:grid-cols-1"}`}>
            <div>
              <h2 className="text-3xl font-bold text-foreground md:text-4xl">{page.introduction.title}</h2>
              <div className="mt-5 space-y-4 text-lg leading-relaxed text-muted-foreground">
                {page.introduction.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              </div>
            </div>
            {page.introduction.image && (
              <img
                src={imageMap[page.introduction.image]}
                alt={page.introduction.imageAlt ?? ""}
                className="aspect-[4/3] w-full rounded-lg object-cover"
                width={800}
                height={600}
                loading="lazy"
                decoding="async"
              />
            )}
          </div>
        </section>

        <section className="bg-secondary py-16 md:py-24">
          <div className="container">
            <div className="mx-auto mb-10 max-w-3xl text-center">
              <p className="mb-2 font-semibold text-primary">Aanbevolen voor de branche</p>
              <h2 className="text-3xl font-bold text-foreground md:text-4xl">Werkkleding voor ieder seizoen</h2>
              <p className="mt-4 text-muted-foreground">
                Stel een praktische kledinglijn samen die aansluit op het werk, het weer en de uitstraling van je bedrijf.
              </p>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {page.products.map((product) => <BranchProductCard key={product.id} selection={product} />)}
            </div>
            <div className="mt-10 text-center">
              <Button variant="outline" asChild>
                <Link to="/producten/workwear">Bekijk alle werkkleding <ArrowRight /></Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="container grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
            <div>
              <h2 className="text-3xl font-bold text-foreground md:text-4xl">{page.teamSection.title}</h2>
              <div className="mt-6 space-y-4 text-lg leading-relaxed text-muted-foreground">
                {page.teamSection.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              </div>
            </div>
            <img
              src={secondaryImage}
              alt={page.secondaryImageAlt}
              className="aspect-[4/3] w-full rounded-lg object-cover object-top"
              width={600}
              height={800}
              loading="lazy"
              decoding="async"
            />
          </div>
        </section>

        <section className="bg-surface-dark py-16 text-surface-dark-foreground md:py-24">
          <div className="container max-w-4xl text-center">
            <h2 className="text-3xl font-bold md:text-4xl">{page.techniqueSection.title}</h2>
            <div className="mt-6 space-y-4 text-lg leading-relaxed text-surface-dark-foreground/80">
              {page.techniqueSection.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </div>
            <div className="mt-8 flex justify-center">
              <Button size="lg" asChild>
                <Link to="/druktechnieken">Bekijk onze druktechnieken <ArrowRight /></Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="container max-w-4xl text-center">
            <h2 className="text-3xl font-bold text-foreground md:text-4xl">{page.quoteSection.title}</h2>
            <p className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-muted-foreground">{page.quoteSection.text}</p>
            <p className="mt-3 font-bold text-foreground">{page.quoteSection.note}</p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Button size="lg" asChild>
                <Link to="/offerte">Vraag gratis een offerte aan <ArrowRight /></Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/contact">Neem contact op</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="bg-secondary py-16 md:py-24" id="veelgestelde-vragen">
          <div className="container max-w-3xl">
            <h2 className="mb-10 text-center text-3xl font-bold text-foreground md:text-4xl">Veelgestelde vragen</h2>
            <Accordion type="single" collapsible className="space-y-3">
              {page.faqs.map((faq, index) => (
                <AccordionItem
                  key={faq.question}
                  value={`branche-faq-${index}`}
                  className="rounded-lg border border-border bg-card px-6 data-[state=open]:border-primary/40"
                >
                  <AccordionTrigger className="text-left font-medium hover:no-underline">{faq.question}</AccordionTrigger>
                  <AccordionContent className="leading-relaxed text-muted-foreground">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default BranchLandingPage;